import { useId } from "react";

type Props = {
  variant?: "analytics" | "abstract" | "waves" | "dots" | "blueprint";
  arcs?: boolean;
  sparkles?: boolean;
};

/**
 * Decorative backdrop for LIGHT sections — the inverse of DarkSectionBackdrop.
 * Subtle radial washes, faint arc rings, sparkle accents, and (analytics variant)
 * a faint line chart + bar chart drawn in SVG. Render as the first child of a
 * section that has `relative isolate overflow-hidden`. All layers sit at -z-10.
 */
export function LightSectionBackdrop({
  variant = "abstract",
  arcs = true,
  sparkles = true,
}: Props) {
  const uid = useId().replace(/:/g, "");
  const arc1 = `lsbArc1-${uid}`;
  const arc2 = `lsbArc2-${uid}`;
  const lineGrad = `lsbLine-${uid}`;
  return (
    <>
      {/* Soft tinted wash */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(55% 70% at 8% 12%, color-mix(in oklab, #6366f1 10%, transparent), transparent 60%), radial-gradient(50% 65% at 92% 22%, color-mix(in oklab, #ec4899 9%, transparent), transparent 60%), radial-gradient(45% 60% at 50% 110%, color-mix(in oklab, #f97316 8%, transparent), transparent 60%)",
        }}
      />
      {arcs && (
        <>
          <svg
            className="pointer-events-none absolute -right-40 -top-40 -z-10 h-[640px] w-[640px] opacity-40"
            viewBox="0 0 640 640"
            fill="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id={arc1} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#6366f1" stopOpacity="0" />
                <stop offset="50%" stopColor="#6366f1" stopOpacity="0.45" />
                <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
              </linearGradient>
            </defs>
            <circle cx="320" cy="320" r="310" stroke={`url(#${arc1})`} strokeWidth="1" />
            <circle
              cx="320"
              cy="320"
              r="270"
              stroke={`url(#${arc1})`}
              strokeWidth="0.75"
              opacity="0.55"
            />
            <circle
              cx="320"
              cy="320"
              r="230"
              stroke={`url(#${arc1})`}
              strokeWidth="0.5"
              opacity="0.4"
            />
          </svg>
          <svg
            className="pointer-events-none absolute -bottom-48 -left-40 -z-10 h-[520px] w-[520px] opacity-35"
            viewBox="0 0 520 520"
            fill="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id={arc2} x1="0" y1="1" x2="1" y2="0">
                <stop offset="0%" stopColor="#f97316" stopOpacity="0.35" />
                <stop offset="60%" stopColor="#ec4899" stopOpacity="0.15" />
                <stop offset="100%" stopColor="#ec4899" stopOpacity="0" />
              </linearGradient>
            </defs>
            <circle cx="260" cy="260" r="250" stroke={`url(#${arc2})`} strokeWidth="1" />
            <circle cx="260" cy="260" r="210" stroke={`url(#${arc2})`} strokeWidth="0.75" />
          </svg>
        </>
      )}
      {variant === "analytics" && (
        <>
          <svg
            className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-auto w-full opacity-[0.09]"
            viewBox="0 0 1200 240"
            preserveAspectRatio="xMidYMin meet"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id={`${lineGrad}-t`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#6366f1" />
                <stop offset="60%" stopColor="#a855f7" />
                <stop offset="100%" stopColor="#ec4899" />
              </linearGradient>
            </defs>
            <g fill="none" stroke={`url(#${lineGrad}-t)`} strokeWidth="2" opacity="0.9">
              <path d="M 0 220 A 220 220 0 0 1 220 0" />
              <path d="M 0 160 A 160 160 0 0 1 160 0" strokeWidth="1.5" opacity="0.7" />
              <path d="M 0 100 A 100 100 0 0 1 100 0" strokeWidth="1" opacity="0.55" />
            </g>
            <g stroke={`url(#${lineGrad}-t)`} strokeWidth="2" opacity="0.8">
              <path d="M 1080 60 H 1110 M 1095 45 V 75" />
              <path d="M 1020 140 H 1044 M 1032 128 V 152" strokeWidth="1.5" />
              <path d="M 1140 180 H 1164 M 1152 168 V 192" strokeWidth="1.5" />
            </g>
          </svg>
          <svg
            className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-auto w-full opacity-[0.09]"
            viewBox="0 0 1200 200"
            preserveAspectRatio="xMidYMax meet"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id={`${lineGrad}-b`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#6366f1" />
                <stop offset="60%" stopColor="#a855f7" />
                <stop offset="100%" stopColor="#ec4899" />
              </linearGradient>
            </defs>
            <g transform="translate(1050,100)" fill={`url(#${lineGrad}-b)`} opacity="0.9">
              {[
                [0, 0, 6],
                [40, -20, 4],
                [80, 10, 5],
                [-30, 30, 3],
                [60, 50, 4],
                [110, -40, 3],
                [20, -60, 4],
                [140, 30, 5],
                [-20, -30, 3],
              ].map(([cx, cy, r], i) => (
                <circle key={i} cx={cx} cy={cy} r={r} />
              ))}
            </g>
            <polygon
              points="80,120 200,40 220,160"
              fill="none"
              stroke={`url(#${lineGrad}-b)`}
              strokeWidth="2"
              opacity="0.7"
            />
          </svg>
        </>
      )}
      {variant === "waves" && (
        <svg
          className="pointer-events-none absolute inset-0 -z-10 h-full w-full opacity-[0.1]"
          viewBox="0 0 1200 600"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id={lineGrad} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#f97316" />
              <stop offset="50%" stopColor="#ec4899" />
              <stop offset="100%" stopColor="#6366f1" />
            </linearGradient>
          </defs>
          {/* Top-left flowing waves */}
          <g fill="none" stroke={`url(#${lineGrad})`} strokeWidth="2">
            <path d="M -20 80 Q 80 30 180 80 T 380 80" opacity="0.9" />
            <path d="M -20 130 Q 80 80 180 130 T 380 130" strokeWidth="1.5" opacity="0.7" />
            <path d="M -20 180 Q 80 130 180 180 T 380 180" strokeWidth="1" opacity="0.55" />
          </g>
          {/* Bottom-right flowing waves (mirrored) */}
          <g fill="none" stroke={`url(#${lineGrad})`} strokeWidth="2">
            <path d="M 820 520 Q 920 470 1020 520 T 1220 520" opacity="0.9" />
            <path d="M 820 470 Q 920 420 1020 470 T 1220 470" strokeWidth="1.5" opacity="0.7" />
            <path d="M 820 420 Q 920 370 1020 420 T 1220 420" strokeWidth="1" opacity="0.55" />
          </g>
          {/* Top-right small hex */}
          <polygon
            points="1100,80 1140,60 1180,80 1180,120 1140,140 1100,120"
            fill="none"
            stroke={`url(#${lineGrad})`}
            strokeWidth="1.5"
            opacity="0.6"
          />
          {/* Bottom-left dots */}
          <g fill={`url(#${lineGrad})`} opacity="0.7">
            {[
              [60, 480, 4],
              [100, 510, 3],
              [140, 480, 5],
              [180, 530, 3],
              [40, 540, 3],
            ].map(([cx, cy, r], i) => (
              <circle key={i} cx={cx} cy={cy} r={r} />
            ))}
          </g>
        </svg>
      )}
      {variant === "dots" && (
        <>
          <svg
            className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-auto w-full opacity-[0.1]"
            viewBox="0 0 1200 240"
            preserveAspectRatio="xMidYMin meet"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id={`${lineGrad}-t`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#a855f7" />
                <stop offset="100%" stopColor="#f97316" />
              </linearGradient>
            </defs>
            <g fill={`url(#${lineGrad}-t)`}>
              {Array.from({ length: 5 }).flatMap((_, r) =>
                Array.from({ length: 5 }).map((__, c) => (
                  <circle
                    key={`tr-${r}-${c}`}
                    cx={1000 + c * 38}
                    cy={60 + r * 38}
                    r={2.5}
                    opacity={0.85 - r * 0.12 - c * 0.05}
                  />
                )),
              )}
            </g>
            <g stroke={`url(#${lineGrad}-t)`} strokeWidth="2" opacity="0.85">
              <path d="M 80 70 H 110 M 95 55 V 85" />
              <path d="M 150 130 H 174 M 162 118 V 142" strokeWidth="1.5" opacity="0.75" />
              <path d="M 50 160 H 70 M 60 150 V 170" strokeWidth="1.2" opacity="0.6" />
            </g>
          </svg>
          <svg
            className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-auto w-full opacity-[0.1]"
            viewBox="0 0 1200 240"
            preserveAspectRatio="xMidYMax meet"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id={`${lineGrad}-b`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#a855f7" />
                <stop offset="100%" stopColor="#f97316" />
              </linearGradient>
            </defs>
            <g fill={`url(#${lineGrad}-b)`}>
              {Array.from({ length: 5 }).flatMap((_, r) =>
                Array.from({ length: 5 }).map((__, c) => (
                  <circle
                    key={`bl-${r}-${c}`}
                    cx={60 + c * 38}
                    cy={40 + r * 38}
                    r={2.5}
                    opacity={0.85 - (4 - r) * 0.12 - c * 0.05}
                  />
                )),
              )}
            </g>
            <g
              fill="none"
              stroke={`url(#${lineGrad}-b)`}
              strokeWidth="2"
              opacity="0.85"
              strokeLinecap="round"
            >
              <path d="M 1060 180 L 1110 130" />
              <path d="M 1090 190 L 1150 130" strokeWidth="1.5" opacity="0.7" />
              <path d="M 1120 200 L 1180 140" strokeWidth="1.2" opacity="0.55" />
            </g>
          </svg>
        </>
      )}
      {variant === "blueprint" && (
        <>
          <svg
            className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-auto w-full opacity-[0.09]"
            viewBox="0 0 1200 240"
            preserveAspectRatio="xMidYMin meet"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id={`${lineGrad}-t`} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#6366f1" />
                <stop offset="100%" stopColor="#ec4899" />
              </linearGradient>
            </defs>
            <g fill="none" stroke={`url(#${lineGrad}-t)`} strokeWidth="2" opacity="0.85">
              <path d="M 30 30 H 200 M 30 30 V 200" />
              <path d="M 60 60 H 170 M 60 60 V 170" strokeWidth="1.2" opacity="0.6" />
              <circle cx="200" cy="30" r="5" />
              <circle cx="30" cy="200" r="5" />
            </g>
            <g stroke={`url(#${lineGrad}-t)`} strokeWidth="1.5" opacity="0.7">
              <path d="M 1080 80 H 1140 M 1110 50 V 110" />
              <circle cx="1110" cy="80" r="14" fill="none" />
            </g>
          </svg>
          <svg
            className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-auto w-full opacity-[0.09]"
            viewBox="0 0 1200 200"
            preserveAspectRatio="xMidYMax meet"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id={`${lineGrad}-b`} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#6366f1" />
                <stop offset="100%" stopColor="#ec4899" />
              </linearGradient>
            </defs>
            <g fill="none" stroke={`url(#${lineGrad}-b)`} strokeWidth="2" opacity="0.85">
              <path d="M 1170 170 H 1000 M 1170 170 V 0" />
              <path d="M 1140 140 H 1030 M 1140 140 V 30" strokeWidth="1.2" opacity="0.6" />
              <circle cx="1000" cy="170" r="5" />
              <circle cx="1170" cy="0" r="5" />
            </g>
            <g
              fill="none"
              stroke={`url(#${lineGrad}-b)`}
              strokeWidth="1.5"
              opacity="0.8"
              strokeLinejoin="round"
            >
              <polygon points="60,140 100,70 130,145" />
              <polygon points="135,160 165,110 185,160" strokeWidth="1.2" opacity="0.65" />
            </g>
          </svg>
        </>
      )}
      {sparkles && (
        <>
          <span
            className="pointer-events-none absolute left-[5%] top-[14%] -z-10 text-xl text-rose-500/50 animate-pulse"
            aria-hidden="true"
          >
            ✦
          </span>
          <span
            className="pointer-events-none absolute left-[8%] bottom-[20%] -z-10 text-2xl text-indigo-500/45 animate-pulse [animation-delay:600ms]"
            aria-hidden="true"
          >
            ✦
          </span>
          <span
            className="pointer-events-none absolute right-[7%] top-[18%] -z-10 text-lg text-fuchsia-500/45 animate-pulse [animation-delay:1200ms]"
            aria-hidden="true"
          >
            ✦
          </span>
          <span
            className="pointer-events-none absolute right-[10%] bottom-[14%] -z-10 text-xl text-orange-500/45 animate-pulse [animation-delay:1800ms]"
            aria-hidden="true"
          >
            ✦
          </span>
        </>
      )}
    </>
  );
}
